import { useMemo, useState, useEffect } from 'react';

import { AppIcons, PopoverFilterMenuItem, PriorityPlaylist } from '@backrs_ui/common-ui';

import {
  useAddSuggestedPlaylistByIdMutation,
  useGetStatusTypesQuery,
  usePutPlaylistMutation,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  PriorityTask,
  PRIORITY_TASK_STATUS_CODES,
  PriorityTaskStatusType,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

export type SortField = 'priority' | 'order' | 'dueDate' | 'status' | 'default' | null;
export type SortDirection = 'asc' | 'desc' | null;

/**
 * COMPARISON FUNCTIONS
 *
 * Compares dates, and always groups nulls at the end
 */
const compareDates = (aDate: string | null, bDate: string | null, direction: number) => {
  const aTime = aDate ? new Date(aDate).getTime() : null;
  const bTime = bDate ? new Date(bDate).getTime() : null;

  if (aTime === null && bTime === null) {
    return 0;
  }

  if (aTime === null) {
    return 1;
  }

  if (bTime === null) {
    return -1;
  }

  return direction * (aTime - bTime);
};

/**
 * Compares two arbitrary values, and always groups nulls at the end
 */
const compareValues = (aValue: number | null, bValue: number | null, direction: number) => {
  if (aValue == null && bValue == null) {
    return 0;
  }
  if (aValue == null) {
    return 1;
  }
  if (bValue == null) {
    return -1;
  }

  return direction * (aValue - bValue);
};

/**
 * Compares statuses, and always groups nulls at the end
 */
const getStatusOrder = (task: PriorityTask, taskStatuses?: PriorityTaskStatusType[]) => {
  const status = taskStatuses?.find((status) => status.code === task.status_code);

  /**
   * is status is not found, assign it a very high number which will push it to the bottom of the list
   */
  const DEFAULT_HIGHEST_STATUS_ORDER = 9999;

  if (!status) {
    return DEFAULT_HIGHEST_STATUS_ORDER;
  }

  switch (status.code) {
    case PRIORITY_TASK_STATUS_CODES.NOT_STARTED:
      return 1;
    case PRIORITY_TASK_STATUS_CODES.IN_PROGRESS:
      return 2;
    case PRIORITY_TASK_STATUS_CODES.COMPLETED:
      return 3;
    default:
      return DEFAULT_HIGHEST_STATUS_ORDER;
  }
};

type UseTaskSortReturn = Readonly<{
  sortedTasks: PriorityTask[];
  sortMenuItems: PopoverFilterMenuItem[];
  toggleSort: (field: SortField) => void;
}>;

/**
 * Determine default sort field based on task arrangement:
 * - If tasks are ordered by their ID values, return 'default'
 * - Else return 'custom'
 */
const getDefaultSortField = (tasksForPlaylist: PriorityTask[] | undefined): SortField => {
  if (!tasksForPlaylist || tasksForPlaylist.length < 2) {
    return 'order';
  }

  // Check if tasks are ordered by their ID values
  const isOrderedById = tasksForPlaylist.every((task, index) => {
    if (index === 0) return true;

    const prevTask = tasksForPlaylist[index - 1];

    return task.id > prevTask.id;
  });

  return isOrderedById ? 'default' : 'order';
};

export const usePlaylistTasksSort = (
  tasksForPlaylist: PriorityTask[] | undefined,
): UseTaskSortReturn => {
  const [sortField, setSortField] = useState<SortField>(() =>
    getDefaultSortField(tasksForPlaylist),
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Update sort field when tasks change
  useEffect(() => {
    setSortField(getDefaultSortField(tasksForPlaylist));
  }, [tasksForPlaylist]);

  const { data: taskStatuses } = useGetStatusTypesQuery(undefined);

  /**
   * Sorts the tasks based on the sort field and direction
   */
  const sortedTasks = useMemo(() => {
    if (!tasksForPlaylist || !sortField || !sortDirection) return tasksForPlaylist || [];

    return [...tasksForPlaylist].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'order':
          return compareValues(a.order, b.order, direction);
        case 'default':
          return direction * (a.id - b.id);
        case 'dueDate':
          return compareDates(a.due_date, b.due_date, direction);
        case 'priority':
          return compareValues(a.priority, b.priority, direction);
        case 'status':
          return direction * (getStatusOrder(a, taskStatuses) - getStatusOrder(b, taskStatuses));
        default:
          return 0;
      }
    });
  }, [tasksForPlaylist, sortField, sortDirection, taskStatuses]);

  /**
   * Toggles the sort field and direction
   */
  const toggleSort = (field: SortField) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDirection('asc');

      return;
    }
    if (sortDirection === 'asc') {
      setSortDirection('desc');

      return;
    }

    if (sortDirection === 'desc') {
      setSortDirection('asc');
    }
  };

  const sortDirectionIcon: AppIcons = sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown';

  const sortMenuItems: PopoverFilterMenuItem[] = [
    {
      label: 'Created',
      value: 'default',
      category: 'sort',
      rightIcon: sortField === 'default' ? sortDirectionIcon : undefined,
      closeOnClick: false,
      onClick: () => toggleSort('default'),
    },
    {
      label: 'Status',
      value: 'status',
      category: 'sort',
      rightIcon: sortField === 'status' ? sortDirectionIcon : undefined,
      closeOnClick: false,
      onClick: () => toggleSort('status'),
    },
    {
      label: 'Priority',
      value: 'priority',
      category: 'sort',
      rightIcon: sortField === 'priority' ? sortDirectionIcon : undefined,
      closeOnClick: false,
      onClick: () => toggleSort('priority'),
    },
    {
      label: 'Due Date',
      value: 'dueDate',
      category: 'sort',
      rightIcon: sortField === 'dueDate' ? sortDirectionIcon : undefined,
      closeOnClick: false,
      onClick: () => toggleSort('dueDate'),
    },
    {
      label: 'Order',
      value: 'order',
      category: 'sort',
      rightIcon: sortField === 'order' ? sortDirectionIcon : undefined,
      closeOnClick: false,
      onClick: () => toggleSort('order'),
    },
  ];

  return { sortedTasks, sortMenuItems, toggleSort };
};

/** Resolves to the user's copy. A draft is already theirs, so it's published rather than cloned. */
export const useAdoptSuggestedPlaybook = () => {
  const [addSuggestedPlaylist, { isLoading: isAddingPlaylist }] =
    useAddSuggestedPlaylistByIdMutation();
  const [putPlaylist, { isLoading: isAcceptingDraft }] = usePutPlaylistMutation();

  const adopt = (playlist: PriorityPlaylist): Promise<PriorityPlaylist> => {
    if (playlist.is_draft === true) {
      return putPlaylist({
        id: playlist.id,
        title: playlist.title,
        description: playlist.description,
        tags: playlist.tags?.map((tag) => tag.title) ?? [],
        is_draft: false,
      }).unwrap();
    }

    return addSuggestedPlaylist(playlist.id).unwrap();
  };

  return { adopt, isAdding: isAddingPlaylist || isAcceptingDraft };
};
